import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import { useNotifications } from 'src/notifications';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import { getApiErrorMessage } from 'src/services/api/utils';
import { GET_PROPERTIES_PATH } from 'src/services/api/properties/constants';
import { normalizeOptions } from 'src/services/api/properties/use-property';
import type {
    IProperty,
    IUpdatePropertyRequest,
    IUpdatePropertyResponse
} from 'src/services/api/properties/types';

export const useUpdateProperty = ({
    id,
    notifyOnSuccess = true,
}: {
    id?: number;
    notifyOnSuccess?: boolean;
}) => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        IProperty,
        IStandardErrorResponse,
        IUpdatePropertyRequest
    >(
        async (payload) => {
            const path = `${GET_PROPERTIES_PATH}/${id}`;
            const res = await apiClient(path, {
                method: 'patch',
                json: { property: payload },
            }).json<IUpdatePropertyResponse>();

            return res.property;
        }, {
            onSuccess: (data) => {
                if (notifyOnSuccess) {
                    addNotification('Property updated', 'success');
                }

                queryClient.setQueryData<IProperty[]>([queryKeys.properties], (oldData) => {
                    if (!oldData) {
                        return [];
                    }

                    return oldData.map((property) => {
                        return property.id === data.id ? data : property;
                    });
                });

                queryClient.setQueryData<IProperty>([queryKeys.properties, data.id, normalizeOptions(undefined)], data);

                // Merge the PATCH response into every cached variant for this property
                // (e.g. the include_ancestors / include_facts queries). The PATCH is not
                // fetched with those options, so its `facts`/`parent` are unpopulated (null).
                // Keep each variant's own separately-fetched `facts`/`parent` so a property
                // edit only overwrites scalar fields — otherwise it would wipe facts that
                // another query (e.g. the share button's include_facts query) just fetched.
                queryClient.setQueriesData<IProperty | undefined>(
                    [queryKeys.properties, data.id],
                    (oldData) => {
                        if (!oldData) {
                            return oldData;
                        }
                        return { ...oldData, ...data, facts: oldData.facts, parent: oldData.parent };
                    },
                );
            }, onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to update property';
                addNotification(errorMessage, 'error');
            }
        });
};
